import logger from '3-foundation/Logging/logger';
import { queryCDA } from '3-foundation/CDA/queryCDA';
import { mapNavigationPageSettings } from '3-foundation/PageContent/Mappers/PageSettings/navigationMapper';
import { DISALLOWED_CONTENT_TYPES } from '1-project/_constants';

export interface NavigationConnectorRequestInterface {
	preview: boolean;
	previewData: Content.PreviewData;
}

export interface NavigationConnectorResponseInterface {
	properties: Record<string, unknown>;
	children: Array<NavigationConnectorResponseInterface>;
	id: number;
	culture: string;
	name: string;
	url: string;
	documentType: string;
	template: string;
	createDate: string;
	sortOrder: number;
}

export const getNavigationFromCDA = async ({
	root,
	levels,
	preview,
	locale,
	siteId,
}: {
	root: Site.Site['root'];
	levels: number;
	preview: boolean;
	locale: string;
	siteId: string;
}): Promise<Navigation.NavigationItem> => {
	const profiler = logger.startTimer();
	if (!root.id) {
		logger.error('Internal Server Error \nSite id is missing');
		throw new Error('Site id is missing');
	}
	const loggingName = 'getNavigation';
	try {
		const limit = '100000';
		const params = new URLSearchParams();
		params.append('fields', 'properties[pageSettings]');
		params.append('take', limit);
		params.append('sort', 'sortOrder:asc');
		DISALLOWED_CONTENT_TYPES.forEach((entry) => {
			params.append('filter', `contentType:!${entry}`);
		});

		const data: {
			total: number;
			items: {
				id: string;
				contentType: string;
				name: string;
				route: { path: string };
				properties: { pageSettings: { items: { content: { contentType: string; properties: { [key: string]: unknown } } }[] } };
			}[];
		} = await queryCDA({
			url: 'umbraco/delivery/api/v2/content',
			urlParams: params,
			siteId,
			locale,
			preview,
			logging: loggingName,
		});
		if (!data) throw new Error('Fetching Navigation data from CDA failed');
		if (data?.items?.length + 100 > Number(limit))
			logger.error('Navigation fetch request might have reached the take limit. Please increase it or change how the navigation is fetched.');

		const filteredNavigation = data.items
			.filter((item) => (item.route.path.split('/').length - 1 <= levels ? item : false))
			.map((filteredItem) => {
				const { id, contentType, name, route, properties } = filteredItem ?? {};
				const navigationPageSettings = properties?.pageSettings?.items?.find((item) => item.content?.contentType === 'navigation');
				const mappedNavigationPageSettings = navigationPageSettings ? mapNavigationPageSettings(navigationPageSettings) : null;
				const { hideInNavigation, navigationTitle } = mappedNavigationPageSettings?.content?.properties ?? {};

				return {
					id,
					contentType,
					name,
					url: route?.path,
					hidden: (hideInNavigation as boolean) ?? false,
					title: (navigationTitle as string) ?? null,
				};
			});
		const sortHierarchy = (items: Navigation.NavigationItem[]): Navigation.NavigationItem[] => {
			const itemsByParent = new Map<string, Navigation.NavigationItem[]>();

			items.forEach((item) => {
				const parentPath = item.url.split('/').slice(0, -1).join('/');
				if (!itemsByParent.has(parentPath)) {
					itemsByParent.set(parentPath, []);
				}
				itemsByParent.get(parentPath)!.push(item);
			});

			const buildTree = (parentPath: string): Navigation.NavigationItem[] => {
				const children = itemsByParent.get(parentPath);
				if (!children) return [];

				return children.map((item) => {
					const descendants = buildTree(item.url);
					return {
						...item,
						descendants: descendants.length > 0 ? descendants : null,
					};
				});
			};

			const rootPath = `/${locale.toLowerCase()}`;
			return buildTree(rootPath);
		};

		const sortedNavigation = sortHierarchy(filteredNavigation);
		const navigationWithRoot: Navigation.NavigationItem = {
			...root,
			descendants: sortedNavigation,
		};

		return navigationWithRoot;
	} catch (error) {
		throw new Error('Could not get Navigation. \nCause: ' + error, { cause: error });
	} finally {
		profiler.done({ message: loggingName, level: 'debug', siteId, locale });
	}
};
